'use client'

import { LineEye } from '@qr-front/icons/line-eye'
import { LineEyeOff } from '@qr-front/icons/line-eye-off'
import { LineLock } from '@qr-front/icons/line-lock'
import { InputField } from '@qr-front/ui/components/InputField'
import { cn } from '@qr-front/ui/utils'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { getQrPasswordErrors } from '../../utils/qrPasswordRules'

const PASSWORD_ERROR_KEY = {
	empty: 'public__qr_password__field__error_empty',
	length: 'public__qr_password__field__error_length',
} as const

type TPasswordFieldProps = {
	name: string
	value: string
	placeholder: string
	error?: string
	onChange: (value: string) => void
	onBlur: () => void
}

const PasswordField = ({
	name,
	value,
	placeholder,
	error,
	onChange,
	onBlur,
}: TPasswordFieldProps) => {
	const [isVisible, setIsVisible] = useState(false)
	const RightIcon = isVisible ? LineEye : LineEyeOff

	return (
		<InputField
			name={name}
			type={isVisible ? 'text' : 'password'}
			autoComplete="new-password"
			spellCheck={false}
			placeholder={placeholder}
			value={value}
			error={error}
			className="min-w-0 flex-1"
			leftIcon={LineLock}
			rightIcon={RightIcon}
			rightOnClick={() => setIsVisible((current) => !current)}
			onChange={(event) => onChange(event.target.value)}
			onBlur={onBlur}
		/>
	)
}

export type TQrPasswordFieldsProps = {
	password: string
	repeat: string
	onPasswordChange: (value: string) => void
	onRepeatChange: (value: string) => void
	submitAttempted?: boolean
	layout?: 'stacked' | 'sideBySide'
}

export const QrPasswordFields = ({
	password,
	repeat,
	onPasswordChange,
	onRepeatChange,
	submitAttempted = false,
	layout = 'stacked',
}: TQrPasswordFieldsProps) => {
	const t = useTranslations()
	const [isPasswordTouched, setIsPasswordTouched] = useState(false)
	const [isRepeatTouched, setIsRepeatTouched] = useState(false)

	const errors = getQrPasswordErrors(password, repeat)

	const passwordError =
		(isPasswordTouched || submitAttempted) && errors.password
			? t(PASSWORD_ERROR_KEY[errors.password])
			: undefined
	const repeatError =
		(isRepeatTouched || submitAttempted) && errors.repeat
			? t('auth__common__input_password_validation__match')
			: undefined

	return (
		<div
			className={cn(
				'flex flex-col gap-4',
				layout === 'sideBySide' && 'tablet:flex-row tablet:gap-12',
			)}
		>
			<PasswordField
				name="qrPassword"
				value={password}
				placeholder={t('public__qr_password__field__placeholder')}
				error={passwordError}
				onChange={onPasswordChange}
				onBlur={() => setIsPasswordTouched(true)}
			/>
			<PasswordField
				name="qrPasswordRepeat"
				value={repeat}
				placeholder={t(
					'public__qr_password__field__repeat_placeholder',
				)}
				error={repeatError}
				onChange={onRepeatChange}
				onBlur={() => setIsRepeatTouched(true)}
			/>
		</div>
	)
}
